/**
 * NOVA's cast + phase registry (presentation layer).
 *
 * The four phases form the journey spine; each has a lead persona, plus two
 * specialists. Personas all carry the single "Nova" brand name and are
 * differentiated only by phase, icon, and avatar. Phase hues are
 * OKLCH strings applied via inline `style` (Tailwind cannot JIT runtime-built
 * arbitrary-color classes). Avatars are bundler imports.
 *
 * Aliases: nova personas, nova cast, phase registry, nova phases
 */
import {
  Compass,
  PenLine,
  Route as RouteIcon,
  TrendingUp,
  type LucideIcon,
} from "lucide-react";

import type { NovaPhase } from "~/lib/shared/nova/phases";
import type { NovaAgentType } from "~/lib/shared/nova/types";

// Re-exported so presentation-layer importers keep a single import site; the
// canonical definition lives in the dependency-free phases module.
export type { NovaPhase } from "~/lib/shared/nova/phases";

export type NovaPhaseDescriptor = {
  id: NovaPhase;
  label: string;
  /** OKLCH string for inline-style hue accents. */
  hue: string;
  purpose: string;
};

/** The journey runway, in order: violet → indigo → purple → magenta. */
export const NOVA_PHASES: readonly NovaPhaseDescriptor[] = [
  {
    id: "diagnose",
    label: "Diagnose",
    hue: "oklch(0.5843 0.2089 280.2)",
    purpose: "See where you stand",
  },
  {
    id: "plan",
    label: "Plan",
    hue: "oklch(0.60 0.24 291)",
    purpose: "Know who you're for, shape the offer",
  },
  {
    id: "build",
    label: "Build",
    hue: "oklch(0.6181 0.257 303.95)",
    purpose: "Make it real",
  },
  {
    id: "grow",
    label: "Grow",
    hue: "oklch(0.58 0.27 312)",
    purpose: "Create your Client Acquisition System",
  },
] as const;

export type NovaPersona = {
  id: string;
  /**
   * Display name. All personas share the single "Nova" brand name (founder
   * decision: distinct character names dilute the brand). Personas are
   * differentiated only by phase and icon — never by a proper name or a role
   * label. The visual identity is the shared `NovaPresence` mark, re-hued per
   * phase (there are no per-persona avatar images).
   */
  name: string;
  phase: NovaPhase;
  kind: "lead" | "specialist";
  icon: LucideIcon;
};

export const NOVA_PERSONAS: readonly NovaPersona[] = [
  { id: "iris", name: "Nova", phase: "diagnose", kind: "lead", icon: Compass },
  { id: "atlas", name: "Nova", phase: "plan", kind: "lead", icon: RouteIcon },
  { id: "vera", name: "Nova", phase: "build", kind: "lead", icon: PenLine },
  { id: "sol", name: "Nova", phase: "grow", kind: "lead", icon: TrendingUp },
  {
    id: "mira",
    name: "Nova",
    phase: "build",
    kind: "specialist",
    icon: PenLine,
  },
  {
    id: "theo",
    name: "Nova",
    phase: "grow",
    kind: "specialist",
    icon: TrendingUp,
  },
] as const;

export function getPhase(id: NovaPhase): NovaPhaseDescriptor | undefined {
  return NOVA_PHASES.find((p) => p.id === id);
}

export function getPhaseLead(phase: NovaPhase): NovaPersona | undefined {
  return NOVA_PERSONAS.find((p) => p.phase === phase && p.kind === "lead");
}

/** Phase → the agent a session in that phase runs. */
export const PHASE_TO_AGENT: Record<NovaPhase, NovaAgentType> = {
  diagnose: "diagnose",
  plan: "plan",
  build: "assets",
  grow: "grow",
};

/** The lead persona's one-line, second-person intro shown in a new session. */
export const PHASE_INTRO: Record<NovaPhase, string> = {
  diagnose:
    "Let's take an honest look at where your coaching business stands — and what to tackle next.",
  plan: "Let's get sharp on exactly who you're for, then shape the offer and positioning that sells it.",
  build:
    "Bring me your goals and we'll turn them into polished, ready-to-publish assets.",
  grow: "Let's turn your offer into a 90-day plan you can run — week by week, in your own numbers.",
};

/** Session agent → phase. Inverse of PHASE_TO_AGENT. */
export const AGENT_TO_PHASE: Record<NovaAgentType, NovaPhase> = {
  diagnose: "diagnose",
  plan: "plan",
  assets: "build",
  grow: "grow",
};

/** Who the coach is in a session with. A neutral "Nova" identity covers a
 * phase without a lead persona so no session renders a broken header. */
export type SessionIdentity =
  | { kind: "persona"; persona: NovaPersona; phase: NovaPhaseDescriptor }
  | { kind: "nova" };

/** Resolve the persona + phase that lead a session, or the Nova fallback. */
export function resolveSessionIdentity(
  agentType: NovaAgentType,
): SessionIdentity {
  const phaseId = AGENT_TO_PHASE[agentType];
  const phase = getPhase(phaseId);
  const persona = getPhaseLead(phaseId);
  if (!phase || !persona) return { kind: "nova" };
  return { kind: "persona", persona, phase };
}

/** The forward step of the journey for the hand-off CTA. */
export type Handoff = {
  nextPhase: NovaPhase;
  nextPersona: NovaPersona;
  verb: string;
};

const HANDOFF_NEXT: Partial<
  Record<NovaPhase, { phase: NovaPhase; verb: string }>
> = {
  diagnose: { phase: "plan", verb: "shape the offer" },
  plan: { phase: "build", verb: "create your assets" },
  build: { phase: "grow", verb: "build your 90-day plan" },
};

/** The next phase to hand off to, or null when there is none. */
export function resolveHandoff(phase: NovaPhase): Handoff | null {
  const next = HANDOFF_NEXT[phase];
  if (!next) return null;
  const nextPersona = getPhaseLead(next.phase);
  if (!nextPersona) return null;
  return { nextPhase: next.phase, nextPersona, verb: next.verb };
}
